import { ChevronRight, Clock, Loader2 } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Alert } from '../atoms/Alert'
import { Badge } from '../atoms/Badge'
import { Icon } from '../atoms/Icon'
import { StatusDot, type StatusDotProps } from '../atoms/StatusDot'
import { CodeBlock } from './CodeBlock'

/**
 * InvocationTraceRow — one stage execution inside a Custom Flow invocation
 * trace. Renders as a `<li>` so a parent `<ul>` composes the full trace.
 *
 * Surfaces:
 *   - Stage name + execution status pill (DS semantic tokens, no raw colours)
 *   - Wall-clock duration with smart unit rollover (`123ms` → `1.23s` → `2m 5s`)
 *   - Optional started-at timestamp
 *   - Collapsible state-diff panel rendered as pretty JSON via DS CodeBlock
 *   - Failure banner via DS Alert when `status === 'failed'` and an
 *     `errorMessage` is provided
 *
 * The chevron itself is the disclosure toggle (a labelled `<button>` with
 * `aria-expanded`) — keyboard focus lands there directly. Both controlled
 * and uncontrolled disclosure are supported via the standard pattern.
 *
 * Composes: Badge (atom), Icon (atom), Alert (atom), CodeBlock (molecule).
 */

export type InvocationTraceStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped'

export interface InvocationTraceStateDiff {
  before?: unknown
  after?: unknown
}

export interface InvocationTraceRowProps {
  /** Stage label as it appears in the builder. */
  stageName: string
  /** Execution status — drives the pill + icon. */
  status: InvocationTraceStatus
  /** ISO 8601 wall-clock start. Surfaced as a muted caption. */
  startedAt?: string
  /** Wall-clock duration in ms. Formatted via {@link formatDuration}. */
  durationMs?: number
  /**
   * Per-key state diff. Each key renders as
   * `{ before: <…>, after: <…> }` in the expanded JSON pane.
   */
  stateDiff?: Record<string, InvocationTraceStateDiff>
  /** Surfaced as a DS Alert when status is 'failed'. */
  errorMessage?: string
  /** Initial disclosure state (uncontrolled). */
  defaultExpanded?: boolean
  /** Notified whenever the disclosure toggles. */
  onToggle?: (expanded: boolean) => void
  /** Outer wrapper classes. */
  className?: string
}

/**
 * formatDuration — humane wall-clock duration:
 *   < 1000ms    → `Nms`            (e.g. "742ms")
 *   < 60000ms   → `N.NNs`          (e.g. "12.34s" — two decimals, trimmed)
 *   ≥ 60000ms   → `Nm Ns`          (e.g. "2m 5s"; seconds dropped when zero)
 *
 * Negative or non-finite inputs return "—".
 */
export function formatDuration(ms: number | undefined | null): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60_000) {
    // 2 decimals, then trim trailing zeros so 5000 → "5s", 5430 → "5.43s".
    const secs = (ms / 1000).toFixed(2).replace(/\.?0+$/, '')
    return `${secs}s`
  }
  const totalSecs = Math.floor(ms / 1000)
  const m = Math.floor(totalSecs / 60)
  const s = totalSecs - m * 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

interface ToneSpec {
  /** StatusDot tone — coloured circle preceding the pill label. */
  dot: NonNullable<StatusDotProps['status']>
  label: string
  /** Pulse indicator override (running state shows a spinner instead). */
  showSpinner?: boolean
}

// Pill is always 'outlined' to keep text contrast against the page background;
// the leading StatusDot carries the semantic colour. The DS Badge tinted-bg
// variants (success/warning/error/info) currently fail axe 4.5:1 at the
// caption font size — outlined + dot is the contrast-safe equivalent.
const STATUS_SPEC: Record<InvocationTraceStatus, ToneSpec> = {
  pending: { dot: 'inactive', label: 'Pending' },
  running: { dot: 'pending', label: 'Running', showSpinner: true },
  succeeded: { dot: 'active', label: 'Succeeded' },
  failed: { dot: 'error', label: 'Failed' },
  skipped: { dot: 'inactive', label: 'Skipped' },
}

/**
 * Pretty-print a state-diff to deterministic JSON. Handles BigInt and
 * sentinel `undefined` (which JSON.stringify would drop) by coercing them
 * to descriptive string placeholders so they survive the round-trip.
 */
function stringifyDiff(diff: Record<string, InvocationTraceStateDiff>): string {
  const safe = (v: unknown): unknown => {
    if (typeof v === 'bigint') return `${v.toString()}n`
    if (v === undefined) return '<undefined>'
    return v
  }
  const normalised: Record<string, { before: unknown; after: unknown }> = {}
  for (const [key, change] of Object.entries(diff)) {
    normalised[key] = { before: safe(change.before), after: safe(change.after) }
  }
  return JSON.stringify(normalised, null, 2)
}

export const InvocationTraceRow = React.forwardRef<HTMLLIElement, InvocationTraceRowProps>(
  (
    {
      stageName,
      status,
      startedAt,
      durationMs,
      stateDiff,
      errorMessage,
      defaultExpanded = false,
      onToggle,
      className,
    },
    ref,
  ) => {
    const [expanded, setExpanded] = React.useState(defaultExpanded)
    const spec = STATUS_SPEC[status]
    const hasDiff = !!stateDiff && Object.keys(stateDiff).length > 0
    const hasError = status === 'failed' && !!errorMessage
    const canExpand = hasDiff || hasError
    const reactPanelId = React.useId()

    const handleToggle = React.useCallback(() => {
      setExpanded((prev) => {
        const next = !prev
        onToggle?.(next)
        return next
      })
    }, [onToggle])

    const formattedStart = React.useMemo(() => {
      if (!startedAt) return null
      const d = new Date(startedAt)
      if (Number.isNaN(d.getTime())) return null
      // HH:MM:SS in the viewer's locale — sufficient for a trace row;
      // the full ISO timestamp lives in title="…" for hover.
      return d.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    }, [startedAt])

    return (
      <li
        ref={ref}
        className={cn(
          'list-none rounded-md border border-ia-border bg-card',
          'transition-colors duration-150',
          status === 'running' && 'border-ia-info/40',
          status === 'failed' && 'border-ia-error/40',
          className,
        )}
      >
        <div className="flex items-center gap-3 px-3 py-2.5">
          <button
            type="button"
            onClick={handleToggle}
            disabled={!canExpand}
            aria-expanded={canExpand ? expanded : undefined}
            aria-controls={canExpand ? reactPanelId : undefined}
            aria-label={
              canExpand
                ? expanded
                  ? 'Collapse stage details'
                  : 'Expand stage details'
                : 'Stage has no details to expand'
            }
            className={cn(
              'inline-flex h-7 w-7 items-center justify-center rounded-pill shrink-0',
              'text-ia-text-tertiary transition-[background-color,color,transform] duration-150',
              'hover:bg-ia-elevated hover:text-ia-text',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent',
              expanded && 'rotate-90 text-ia-text',
            )}
          >
            <Icon icon={ChevronRight} size="sm" />
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="truncate text-body-md font-medium text-ia-text">{stageName}</span>
              {formattedStart && (
                <span
                  className="font-mono text-caption tabular-nums text-ia-text-secondary"
                  title={startedAt}
                >
                  {formattedStart}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span
              className="font-mono text-caption tabular-nums text-ia-text-secondary"
              title={`Duration: ${formatDuration(durationMs)}`}
            >
              <Icon icon={Clock} size="xs" className="mr-1 inline-block align-text-bottom" />
              {formatDuration(durationMs)}
            </span>
            <Badge variant="outlined" size="sm" aria-label={`Status: ${spec.label}`}>
              {spec.showSpinner ? (
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} aria-hidden="true" />
              ) : (
                <StatusDot status={spec.dot} size="sm" aria-hidden="true" />
              )}
              {spec.label}
            </Badge>
          </div>
        </div>

        {canExpand && expanded && (
          <div
            id={reactPanelId}
            className="border-t border-ia-border px-3 py-3 flex flex-col gap-3"
          >
            {hasError && (
              <Alert tone="error" title="Stage failed">
                {errorMessage}
              </Alert>
            )}
            {hasDiff && (
              <div className="flex flex-col gap-1.5">
                <span className="text-overline font-semibold uppercase tracking-wider text-ia-text-tertiary">
                  State diff
                </span>
                <CodeBlock language="json" code={stringifyDiff(stateDiff!)} showCopy />
              </div>
            )}
          </div>
        )}
      </li>
    )
  },
)
InvocationTraceRow.displayName = 'InvocationTraceRow'
